import { ref, computed, watch, onBeforeUnmount } from '@vue/composition-api'
import { getDatasetModule } from '../config/datasets'
import {
  DEFAULT_CURRENT_STATUS_LABEL,
  MAX_CURRENT_STATUS_IDS,
  buildHistoryRowsUrl,
  buildRecordsUrl,
  historiesOf,
  pickLatestRowIds,
  referencedHistories,
  removeLatestRowsRestriction,
  restrictUrlToLatestRows,
} from '../utils/currentStatus'

const READ_DEBOUNCE_MS = 350
const READ_REUSE_MS = 60 * 1000
const MIN_LIVE_REREAD_SECONDS = 60

const queryOf = url => new URLSearchParams(String(url || '').split('/?')[1] || '')

const countIds = lists => lists.reduce((total, list) => total + list.length, 0)

/** The ids read for each history, keyed by its column. */
const byHistoryColumn = (histories, lists) => Object.fromEntries(
  histories.map((history, index) => [history.historyColumn, lists[index]]),
)

/**
 * "Current status only" for datasets whose status (or assignee, or last
 * change) lives in a history table, one row per change, declared by the module
 * as `currentStatus`. The service joins the whole history, so a chart that
 * reads it counts a record once per change it went through. The fix the
 * backend should make is a join on the latest row; until then this resolves it
 * in two steps, like the top N: read the history row ids of the records in
 * scope, keep the latest per record and hand them back to the chart as a
 * `where` on the history id. A dataset with more than one history (plannings)
 * gets one read and one `where` per history the chart reads.
 *
 * That `where` also drops the records with no history row at all (no operator
 * keeps NULLs, measured). Charts accept it and say so in the panel; downloads
 * do not, see applyCurrentStatusTo.
 *
 * On by default (only a saved chart that turned it off keeps it off). The ids
 * are never persisted: they change with every status change.
 *
 * «Solo abiertos» (module key `closedStatus`, off by default, persisted as
 * `openOnly`) also leaves out the records whose current status is closed, in
 * any grouping: the chart gets a `notin` on the status column, which on a
 * status kept in a history only means "current" together with the latest-row
 * restriction, so it keeps that restriction on while it is on.
 */
export function useCurrentStatus({ selectedDataset, refreshInterval, saved = {} }) {
  const enabled = ref(saved.currentStatusOnly !== false)
  const openOnly = ref(saved.openOnly === true)
  // Latest row ids keyed by history column.
  const latest = ref({})
  const loading = ref(false)
  const error = ref('')
  // Ids only apply to the URL they were read for. The source is read straight
  // from the bound computed (not copied by a watcher, which would lag a tick
  // and let the previous ids reach a URL built from the new filters).
  const latestSource = ref('')
  const bound = ref(false)
  let sourceUrl = null
  const currentSource = () => (bound.value && sourceUrl ? sourceUrl.value || '' : '')

  const config = computed(() => getDatasetModule(selectedDataset.value)?.currentStatus || null)
  const closedConfig = computed(() => getDatasetModule(selectedDataset.value)?.closedStatus || null)
  const offered = computed(() => Boolean(config.value))
  const openOnlyOffered = computed(() => Boolean(closedConfig.value))
  const closedValues = computed(() => closedConfig.value?.values || [])
  const openOnlyActive = computed(() => openOnly.value && openOnlyOffered.value)
  // «Solo abiertos» needs one row per record, so it keeps this on.
  const locked = computed(() => openOnlyActive.value && offered.value)
  const active = computed(() => offered.value && (enabled.value || openOnlyActive.value))
  const label = computed(() => config.value?.label || DEFAULT_CURRENT_STATUS_LABEL)

  // Contract consumed by useChartUrl.
  const currentStatus = computed(() => ({
    config: active.value ? config.value : null,
    latest: active.value && latestSource.value && latestSource.value === currentSource()
      ? latest.value
      : null,
  }))

  // Also consumed by useChartUrl, apart from currentStatus: the unrestricted
  // URL needs it, and currentStatus reads that URL.
  const openOnlyFilter = computed(() => (openOnlyActive.value ? closedConfig.value : null))

  /** Whether the chart on screen is restricted right now. */
  const applied = computed(() => Object.values(currentStatus.value.latest || {}).some(ids => ids.length))

  /**
   * Whether the chart on screen reads a history at all. When it does not (e.g.
   * audits by REMS), it has no repeated rows and the option cannot change it;
   * the panel says so instead of leaving a switch that seems to do nothing.
   */
  const relevant = computed(() => offered.value
    && referencedHistories(queryOf(currentSource()), selectedDataset.value, config.value).length > 0)

  /** Labels of the fields and filters that read a history: where the option shows. */
  const affects = computed(() => {
    const module = getDatasetModule(selectedDataset.value)
    const tables = historiesOf(config.value).flatMap(history => history.tables)
    const onHistory = column => tables.includes(String(column || '').split('.')[0])
    return [...new Set([
      ...(module?.fields || []).filter(field => onHistory(field.key)).map(field => field.label),
      ...(module?.filters || []).filter(filter => onHistory(filter.whereColumn)).map(filter => filter.label),
    ])]
  })

  /** The histories a URL reads, when the option applies to it. */
  const historiesRead = url => (active.value && url
    ? referencedHistories(queryOf(url), selectedDataset.value, config.value)
    : [])
  const needsRead = url => historiesRead(url).length > 0

  // Reads are shared by the chart and the downloads and reused for a minute,
  // so flipping a filter back and forth does not re-read the history.
  const reads = new Map()
  const readRows = (readUrl, { fresh = false } = {}) => {
    const cached = reads.get(readUrl)
    if (!fresh && cached && Date.now() - cached.at < READ_REUSE_MS) return cached.request
    const request = fetch(readUrl).then((response) => {
      // The service answers an empty read with 404 "No data".
      if (response.status === 404) return []
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      return response.json()
    })
    reads.set(readUrl, { request, at: Date.now() })
    request.catch(() => reads.delete(readUrl))
    return request
  }

  const readLatestIds = async (url, history, options) => {
    const readConfig = config.value
    const rows = await readRows(buildHistoryRowsUrl(url, selectedDataset.value, readConfig, history), options)
    return pickLatestRowIds(rows, readConfig, history)
  }

  const clearLatest = () => {
    latest.value = {}
    latestSource.value = ''
  }

  const resetIfInactive = () => {
    if (active.value) return
    clearLatest()
    error.value = ''
  }

  const setEnabled = (next) => {
    enabled.value = next
    resetIfInactive()
  }

  const setOpenOnly = (next) => {
    openOnly.value = next
    resetIfInactive()
  }

  watch(selectedDataset, () => {
    clearLatest()
    error.value = ''
  })

  /**
   * The same restriction for a download URL (XLSX, PDF), resolved on demand
   * because the spreadsheet reads the history even when the chart does not.
   * Only applied when every record in scope has a history row: a spreadsheet
   * must not lose records, and the restriction would drop those without one
   * (56 of 61 BAU answers on Banamex staging). The API already keeps one row
   * per reference, so the unrestricted file has no duplicates either, only the
   * first status instead of the current one. Any failure returns the URL
   * untouched, which is what downloads did before.
   */
  const applyCurrentStatusTo = async (chartDerivedUrl) => {
    // A download built from a restricted chart inherits its restriction; the
    // decision below is the download's own.
    const url = config.value ? removeLatestRowsRestriction(chartDerivedUrl, config.value) : chartDerivedUrl
    const histories = historiesRead(url)
    if (!histories.length) return url
    try {
      const [records, ...lists] = await Promise.all([
        readRows(buildRecordsUrl(url, selectedDataset.value, config.value)),
        ...histories.map(history => readLatestIds(url, history)),
      ])
      const total = countIds(lists)
      if (!total || total > MAX_CURRENT_STATUS_IDS) return url
      // Every record in scope needs a row in each history the download reads.
      // Not with «Solo abiertos»: its `notin` already leaves out the records
      // without status, and unrestricted it would let in a closed record
      // through an older, open status row.
      const losesRecords = !Array.isArray(records) || lists.some(ids => records.length > ids.length)
      if (losesRecords && !openOnlyActive.value) return url
      return restrictUrlToLatestRows(url, histories, byHistoryColumn(histories, lists))
    } catch (e) {
      console.warn('[ChartsEmbedder] Current-status read for a download failed:', e)
      return url
    }
  }

  /**
   * Wire the read to the chart URL without either restriction. Bound after
   * useChartUrl exists, which itself consumes `currentStatus`.
   */
  const bindCurrentStatusSource = (chartUrlUnrestricted) => {
    let debounceTimer = null
    let liveTimer = null
    let latestRequestId = 0

    sourceUrl = chartUrlUnrestricted
    bound.value = true

    const refresh = async ({ fresh = false } = {}) => {
      const source = sourceUrl.value
      const histories = historiesRead(source)
      if (!histories.length) {
        latestRequestId += 1
        clearLatest()
        error.value = ''
        loading.value = false
        return
      }

      const requestId = ++latestRequestId
      loading.value = true
      try {
        const lists = await Promise.all(histories.map(history => readLatestIds(source, history, { fresh })))
        if (requestId !== latestRequestId) return
        const total = countIds(lists)
        if (total > MAX_CURRENT_STATUS_IDS) {
          clearLatest()
          error.value = 'Demasiados registros: se cuentan todos los cambios'
          return
        }
        error.value = ''
        // Nothing to restrict (an empty `in` answers HTTP 500): the chart stays as is.
        latest.value = byHistoryColumn(histories, lists)
        latestSource.value = total ? source : ''
      } catch (e) {
        console.warn('[ChartsEmbedder] Current-status read failed:', e)
        if (requestId !== latestRequestId) return
        // A failed live re-read keeps the ids it already had.
        if (fresh && latestSource.value === source) return
        // Unrestricted rather than an arbitrary subset.
        clearLatest()
        error.value = 'No se pudo leer el historial'
      } finally {
        if (requestId === latestRequestId) loading.value = false
      }
    }

    // A live chart refreshes itself, but the ids would not: a record that
    // changes keeps its old row in the list. Re-read on the chart's own cadence
    // (at least a minute); the chart URL only changes, and the chart only
    // reloads, when the ids do.
    const scheduleLiveReread = () => {
      clearInterval(liveTimer)
      liveTimer = null
      if (!needsRead(sourceUrl.value) || queryOf(sourceUrl.value).get('live') !== 'true') return
      const seconds = Math.max(Number(refreshInterval?.value) || 0, MIN_LIVE_REREAD_SECONDS)
      liveTimer = setInterval(() => refresh({ fresh: true }), seconds * 1000)
    }

    watch(
      () => [active.value, sourceUrl.value].join('|'),
      () => {
        clearTimeout(debounceTimer)
        debounceTimer = setTimeout(() => {
          refresh()
          scheduleLiveReread()
        }, READ_DEBOUNCE_MS)
      },
      { immediate: true },
    )

    onBeforeUnmount(() => {
      clearTimeout(debounceTimer)
      clearInterval(liveTimer)
    })
  }

  return {
    currentStatusEnabled: enabled,
    currentStatusOffered: offered,
    currentStatusLocked: locked,
    currentStatusLabel: label,
    currentStatusApplied: applied,
    currentStatusRelevant: relevant,
    currentStatusAffects: affects,
    currentStatusLoading: loading,
    currentStatusError: error,
    currentStatus,
    setCurrentStatusEnabled: setEnabled,
    openOnlyEnabled: openOnly,
    openOnlyOffered,
    openOnlyClosedValues: closedValues,
    openOnlyFilter,
    setOpenOnlyEnabled: setOpenOnly,
    applyCurrentStatusTo,
    bindCurrentStatusSource,
  }
}
